/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useState, PropsWithChildren, useEffect } from "react";
import { AuthApi } from "client";
import { userContext } from "context/UserContext";
import { useAuth0 } from "@auth0/auth0-react";
import { SpinnerBack } from "./SpinnerBack";
import { SystemErrorDialog } from "./SystemErrorDialog";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import { useCallApi } from "hooks/CallApiHook";
import { Const } from "const";

export const PermissionToken: FC<PropsWithChildren> = ({ children }) => {
  const { isLoading, isAuthenticated } = useAuth0();
  const [authApi, cancelAuthApi] = useApiWithTimeout(
    AuthApi,
    Const.operationTimeout.updateToken
  );
  const { loading, error, value, retry } = useCallApi(
    async () => {
      if (isAuthenticated) {
        return authApi.updateToken();
      }
    },
    cancelAuthApi,
    [authApi, cancelAuthApi, isAuthenticated]
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!loading && error) {
      setOpen(true);
    }
  }, [loading, error]);

  if (open) {
    return (
      <SystemErrorDialog
        open={open}
        error={error}
        onClose={() => {
          setOpen(false);
          retry();
        }}
      />
    );
  }

  if (isLoading || !isAuthenticated || !value) {
    return <SpinnerBack />;
  }

  return (
    <userContext.Provider
      value={{
        email: value.email,
        roleName: value.roleName,
        firstName: value.firstName,
        lastName: value.lastName,
      }}
    >
      {children}
    </userContext.Provider>
  );
};
